import useUploadSlideImage from "MyDashboards/hooks/useUploadSlideImage";
import useDashboardEditor from "MyDashboards/state/editor";
import { ActiveEditAction, Element } from "MyDashboards/state/editorTypes";
import { calculateAspectRatioFit } from "MyDashboards/utils/utils";
import * as CSS from "csstype";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { Position, ResizableDelta, Rnd } from "react-rnd";
import box from "../Grid/box";
import {
  ReactRNDDragType,
  disableSnap,
  enableSnap,
  getLastSnapState,
  getSnapMode,
  setSnapItemCoordinates,
  setSnapItemSize,
} from "../Grid/grid";
import ContextMenu from "./ContextMenu";
import Overlay from "./OverlayItems";
import usePaste from "./usePaste";
type DraggableData = {
  node: HTMLElement;
  x: number;
  y: number;
  deltaX: number;
  deltaY: number;
  lastX: number;
  lastY: number;
};

const RenderSelectedSlide = (props: { scale?: number }) => {
  const setSelectedElement = useDashboardEditor((s) => s.setSelectedElement);
  const editorWidth = useDashboardEditor((e) => e.editorWidth);
  const selectedElementId = useDashboardEditor((s) => s.selectedElementId);
  const selectedElements = useDashboardEditor((s) => s.selectedElements);
  const selectedPageId = useDashboardEditor((s) => s.selectedPageId);
  const cloneActiveElement = useDashboardEditor((s) => s.cloneActiveElement);
  const selectedPage = useDashboardEditor((s) =>
    s.pages.find((p) => p.id === s.selectedPageId)
  );
  const specificElementProperties = useDashboardEditor(
    (s) => s.setElementProperties
  );
  const setDragged = useDashboardEditor((s) => s.setDragged);
  const setDragInfo = useDashboardEditor((s) => s.setDragInfo);
  const setActiveElement = useDashboardEditor((s) => s.setSelectedElement);
  const editedElement = useDashboardEditor((s) => s.editedElement);
  const setElementProperties = useDashboardEditor(
    (s) => s.setSelectedElementProperties
  );
  const setPageProps = useDashboardEditor((s) => s.setPageProperties);
  const setEditedElement = useDashboardEditor((s) => s.setEditAction);
  const pageRef = React.useRef<HTMLDivElement>(null);
  const isDragOn = useDashboardEditor((s) => s.isDragged);
  const resizeGridDef: [number, number] = [1, 1];
  const removeElement = useDashboardEditor((s) => s.removeSelectedElement);
  const undo = useDashboardEditor((s) => s.undo);
  const redo = useDashboardEditor((s) => s.redo);
  const moveZOrder = useDashboardEditor((s) => s.moveZOrder);
  const alignElements = useDashboardEditor((s) => s.alignBorders);
  const addElement = useDashboardEditor((s) => s.addElement);
  const addPage = useDashboardEditor((s) => s.addPage);
  const dragGridDef: [number, number] = [1, 1];
  const editorMode = useDashboardEditor((s) => s.mode);
  const moveZ = useDashboardEditor((s) => s.moveZOrder);
  const [coords, setCoords] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isContextMenuOpen, setContextMenuOpen] = React.useState(false);
  const { scale } = props;
  const hasUnsaved = useDashboardEditor((e) => e.hasUnsavedChanges);
  const setUnsaved = useDashboardEditor((e) => e.setUnsavedChanges);
  const [unsavedSet, setUnsavedSet] = React.useState(0);
  const ctx = useAppContext();

  const {
    uploadSlideImage,
    isLoading: isUploadLoading,
    isError: isUploadError,
    data: uploadedFileRef,
  } = useUploadSlideImage();
  const { t } = useTranslation();
  const selectedElement = useDashboardEditor((s) => {
    const [page] = s.pages.filter((p) => p.id === s.selectedPageId);
    if (page) {
      return page.elements.filter((el) => el.id === s.selectedElementId).pop();
    }
  });
  const editorHeight = React.useMemo(() => {
    if (selectedPage) {
      return (editorWidth * selectedPage?.height) / selectedPage?.width;
    }
    return 1;
  }, [selectedPage, editorWidth]);

  React.useEffect(() => {
    const unloadPage = (e: any) => {
      console.log("beforeunload unload page");
      /*
      if (!hasUnsaved()) {
        return undefined;
      }
      e.preventDefault();
      return 'You have unsaved changes, are you sure you want to exit?';
      */
    };

    console.log("beforeunload registered");
    window.addEventListener("beforeunload", unloadPage);

    return () => {
      console.log("beforeunload unregistered");

      setTimeout(() => {
        window.removeEventListener("beforeunload", unloadPage);
      }, 100);
    };
  }, []);

  React.useEffect(() => {
    const iv = window.setTimeout(() => {
      console.log("beforeunload resetting the unsaved");
      setUnsaved(false);
      setUnsavedSet((v) => v + 1);
    }, 300);
    return () => {
      window.clearTimeout(iv);
    };
  }, []);

  React.useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (editedElement) {
        return;
      }
      console.log(event);
      if (event.key === "Delete" || event.key === "Backspace") {
        removeElement();
      }
      if (event.ctrlKey && event.key === "z") {
        undo();
      }
      if (event.ctrlKey && event.key === "x") {
        if (selectedElement) {
          navigator.clipboard.writeText(
            JSON.stringify(selectedElement, null, 2)
          );
          removeElement();
        }
      }
      if (event.ctrlKey && event.key === "s") {
        alert("Save"); // eslint-disable-line
        event.preventDefault();
      }

      if (event.ctrlKey && event.shiftKey && event.key === "(") {
        console.log("z order [");
        moveZOrder(1);
      }
      if (event.ctrlKey && event.shiftKey && event.key === ")") {
        console.log("z order ]");
        moveZOrder(-1);
      }
      if (event.ctrlKey && event.key === "y") {
        redo();
      }
    };
    document.addEventListener("keyup", listener);
    return () => {
      document.removeEventListener("keyup", listener);
    };
  }, [editedElement, selectedElement]);

  const handleCornerStyles = React.useMemo(() => {
    return {
      width: "14px",
      height: "14px",
      backgroundColor: "white",
      borderRadius: "50px",
      border: "1px solid #808080",
    };
  }, []);
  const isDragged = React.useCallback(
    (id: string) => {
      if (id === selectedElementId) return true;
      return selectedElements.includes(id);
    },
    [selectedElementId, selectedElements]
  );

  const selectedSlide = useDashboardEditor((s) => {
    const [page] = s.pages.filter((p) => p.id === s.selectedPageId);
    if (page) {
      return page;
    }
  });

  const activeElements = useDashboardEditor((s) => {
    const ids = () => {
      if (s.selectedElements.length > 0) return s.selectedElements;
      if (s.selectedElementId) return [s.selectedElementId];
      return [];
    };
    return ids()
      .map((id) =>
        s.pages
          .find((p) => p.id === s.selectedPageId)
          ?.elements.find((el) => el.id === id)
      )
      .filter((v) => v) as Element[];
  });

  const sortedElements = React.useMemo(() => {
    if (selectedSlide && selectedSlide.elements) {
      const indexMap: Record<string, number> = {};

      selectedSlide.elements.forEach((e, i) => {
        indexMap[e.id] = i;
      });

      return [...selectedSlide.elements].sort((a, b) => {
        if (a.id === selectedElementId) return 1;
        if (b.id === selectedElementId) return -1;
        return indexMap[a.id] - indexMap[b.id];
      });
    }
    return [];
  }, [selectedSlide, selectedElementId]);

  /**
   * WARNING: do not immediately add data to the editor
   */
  React.useEffect(() => {
    if (editedElement?.action === "paste") {
      setElementProperties({
        component: editedElement.data,
      });
    }
  }, [editedElement]);

  React.useEffect(() => {
    const onCopy = (e: ClipboardEvent) => {
      if (editedElement) {
        return;
      }
      if (selectedElement) {
        e.clipboardData?.setData(
          "text",
          JSON.stringify(selectedElement, null, 2)
        );
      }
      if (!selectedElement && selectedPage) {
        e.clipboardData?.setData("text", JSON.stringify(selectedPage, null, 2));
      }
      e.preventDefault();
    };
    document.addEventListener("copy", onCopy);
    return () => {
      document.removeEventListener("copy", onCopy);
    };
  }, [
    pageRef.current,
    selectedElement,
    selectedSlide,
    selectedPageId,
    selectedElementId,
    editedElement,
  ]);

  usePaste(
    async (type, value) => {
      console.log("PASTE", type, value, selectedElement);
      if (type === "text") {
        try {
          const obj = JSON.parse(value) as ActiveEditAction;
          const actions = ["newComponent", "search", "paste", "edit"];
          if (actions.includes(obj.action)) {
            setEditedElement(obj);
          } else {
            const maybeElement = obj as any;
            if (maybeElement.id && maybeElement.type) {
              // assume this is new element
              addElement(maybeElement);
            }
            if (maybeElement.id && maybeElement.elements) {
              // assume this is new element
              addPage(maybeElement);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      if (type === "image") {
        const res = await uploadSlideImage(value);
        const newDimensions = calculateAspectRatioFit(
          res?.newWidth as number,
          res?.newHeight as number,
          selectedElement?.width as number,
          selectedElement?.height as number
        );
        setElementProperties({
          width: newDimensions.width,
          height: newDimensions.height,
          component: undefined,
          rendered: {
            type: "image",
            pngRef: res?.ref as string,
          },
        });
      }
    },
    [selectedElement, selectedSlide, selectedPageId, selectedElementId]
  );

  return (
    <>
      <div
        id={selectedPageId}
        ref={pageRef}
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        style={{
          // transform: 'scale(0.5)',
          transformOrigin: "left top",
          width: `${selectedSlide?.width || 0}px`,
          height: `${selectedSlide?.height || 0}px`,
          position: "relative",
        }}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
        onClick={() => {
          if (!editedElement) {
            setActiveElement(undefined);
          }
        }}
      >
        <Overlay width={editorWidth} />

        {selectedSlide
          ? sortedElements.map((el) => {
              const gridElements = () => {
                const list = sortedElements
                  .filter((e) => e.id !== el.id)
                  .map(box.fromItem);
                list.push(
                  box.fromItem({
                    id: "window",
                    x: 0,
                    y: 0,
                    width: editorWidth,
                    height: editorHeight,
                  })
                );
                return list;
              };
              const disableDrag =
                el.isStatic ||
                (el.id !== selectedElementId &&
                  !selectedElements.find((v) => v === el.id));
              const elOnClick = (e: MouseEvent) => {
                if (
                  el.id === selectedElement?.id ||
                  activeElements.find((e) => e.id === el.id)
                ) {
                  if (e.detail !== 2) {
                    e.stopPropagation();
                    return;
                  }
                }
                setSelectedElement(el.id, e.ctrlKey);
                e.stopPropagation();
              };
              const elOnDrag = (ev: any, data: DraggableData) => {
                // console.log('onDrag', ev, data);
                setDragged(true);
                setDragInfo({ x: data.x, y: data.y });
                setSnapItemCoordinates(data.x, data.y);
              };
              const elOnDragStart = (ev: any, data: DraggableData) => {
                enableSnap(
                  "move",
                  box.fromItem(el),
                  gridElements()
                  // sortedElements.filter(e => e.id !== el.id).map(box.fromItem)
                );

                setDragged(true);
                setDragInfo({
                  startX: data.x,
                  startY: data.y,
                  x: data.x,
                  y: data.y,
                });
                // setSelectedElement(el.id);
              };
              const elOnDragEnd = (ev: any, data: DraggableData) => {
                setSnapItemCoordinates(data.x, data.y);
                disableSnap();

                setDragged(false);
                setDragInfo({ x: data.x, y: data.y });

                if (
                  selectedPage &&
                  activeElements &&
                  activeElements.length > 0
                ) {
                  const thisEl = selectedPage.elements.find(
                    (e) => e.id === el.id
                  );

                  const snap = getLastSnapState();
                  activeElements.forEach((singleEl) => {
                    if (thisEl) {
                      let x = singleEl.x + data.x - thisEl.x;
                      let y = singleEl.y + data.y - thisEl.y;
                      if (snap && snap.lastTransformResult) {
                        const { lastTransformResult } = snap;
                        const { delta } = lastTransformResult;
                        x += delta.x || 0;
                        y += delta.y || 0;
                      }
                      specificElementProperties(selectedPageId, singleEl.id, {
                        x: x,
                        y: y,
                      });
                    }
                  });
                } else {
                  setElementProperties({
                    x: data.x,
                    y: data.y,
                  });
                }
              };
              const elOnDrop = (e: any) => {
                try {
                  const obj = JSON.parse(
                    e.dataTransfer.getData("Text")
                  ) as ActiveEditAction;

                  if (el.id) {
                    setSelectedElement(el.id);
                  }
                  setEditedElement(obj);
                } catch (e) {
                  console.error(e);
                }
              };
              const elOnResizeStart = (ev: any, dir: string) => {
                console.log("clOnResizeStart ", ev, dir);
                const mode = getSnapMode(dir as ReactRNDDragType);

                enableSnap(mode, box.fromItem(el), gridElements());
                setDragged(true);
                setSelectedElement(el.id);
                setDragInfo({ startX: el.x, startY: el.y, x: el.x, y: el.y });
              };

              const elOnResize = (
                ev: any,
                dir: ReactRNDDragType,
                ref: any,
                delta: ResizableDelta,
                position: Position
              ) => {
                console.log("elOnResize", delta, position);
                setSnapItemCoordinates(position.x, position.y);
                setSnapItemSize(
                  el.width + delta.width,
                  el.height + delta.height
                );
                setDragInfo({
                  x: position.x,
                  y: position.y,
                  width: el.width + delta.width,
                  height: el.height + delta.height,
                });
              };

              const elOnResizeStop = (
                ev: any,
                dir: ReactRNDDragType,
                ref: any,
                delta: ResizableDelta,
                position: Position
              ) => {
                setDragged(false);
                setSnapItemCoordinates(position.x, position.y);
                setSnapItemSize(
                  el.width + delta.width,
                  el.height + delta.height
                );
                disableSnap();
                // different modes
                const snap = getLastSnapState();
                const { lastTransformResult } = snap;
                if (selectedElement && lastTransformResult) {
                  const { delta: transformDelta } = lastTransformResult;
                  if (dir === "top") {
                    setElementProperties({
                      y:
                        selectedElement.y -
                        delta.height +
                        ((transformDelta && transformDelta?.y) || 0),
                      height:
                        delta.height +
                        selectedElement?.height +
                        ((transformDelta && transformDelta?.height) || 0),
                    });
                    return;
                  }

                  if (dir === "left") {
                    setElementProperties({
                      x:
                        selectedElement.x -
                        delta.width +
                        ((transformDelta && transformDelta?.x) || 0),
                      width:
                        delta.width +
                        selectedElement?.width +
                        ((transformDelta && transformDelta?.width) || 0),
                    });
                    return;
                  }

                  if (dir === "topRight") {
                    setElementProperties({
                      y:
                        selectedElement.y -
                        delta.height +
                        ((transformDelta && transformDelta?.y) || 0),
                      width:
                        delta.width +
                        selectedElement?.width +
                        ((transformDelta && transformDelta?.width) || 0),
                      height:
                        delta.height +
                        selectedElement?.height +
                        ((transformDelta && transformDelta?.height) || 0),
                    });
                    return;
                  }
                  if (dir === "bottomLeft") {
                    setElementProperties({
                      x:
                        selectedElement.x -
                        delta.width +
                        ((transformDelta && transformDelta?.x) || 0),
                      width:
                        delta.width +
                        selectedElement?.width +
                        ((transformDelta && transformDelta?.width) || 0),
                      height:
                        delta.height +
                        selectedElement?.height +
                        ((transformDelta && transformDelta?.height) || 0),
                    });
                    return;
                  }

                  if (dir === "topLeft") {
                    setElementProperties({
                      x:
                        selectedElement.x -
                        delta.width +
                        ((transformDelta && transformDelta?.x) || 0),
                      y:
                        selectedElement.y -
                        delta.height +
                        ((transformDelta && transformDelta?.y) || 0),
                      width:
                        delta.width +
                        selectedElement?.width +
                        ((transformDelta && transformDelta?.width) || 0),
                      height:
                        delta.height +
                        selectedElement?.height +
                        ((transformDelta && transformDelta?.height) || 0),
                    });
                    return;
                  }
                  setElementProperties({
                    width:
                      delta.width +
                      selectedElement?.width +
                      ((transformDelta && transformDelta?.width) || 0),
                    height:
                      delta.height +
                      selectedElement?.height +
                      (transformDelta?.height || 0),
                  });
                }
              };

              let renderStyleParams: CSS.Properties = {};
              const addStyle = (params: CSS.Properties) => {
                renderStyleParams = { ...renderStyleParams, ...params };
              };

              return (
                <Rnd
                  scale={scale}
                  position={{ x: el.x, y: el.y }}
                  onContextMenu={(e: any) => {
                    console.log(
                      activeElements.map((e) => e.id),
                      "vs",
                      el.id
                    );
                    if (!activeElements.map((e) => e.id).includes(el.id)) {
                      setSelectedElement(el.id);
                    }

                    setCoords({ x: e.clientX, y: e.clientY });
                    setContextMenuOpen(true);
                    e.preventDefault();
                  }}
                  id={el.id}
                  style={renderStyleParams}
                  key={el.id}
                  resizeGrid={resizeGridDef}
                  dragGrid={dragGridDef}
                  resizeHandleStyles={{
                    topRight: isDragged(el.id) ? handleCornerStyles : undefined,
                    topLeft: isDragged(el.id) ? handleCornerStyles : undefined,
                    bottomRight: isDragged(el.id)
                      ? handleCornerStyles
                      : undefined,
                    bottomLeft: isDragged(el.id)
                      ? handleCornerStyles
                      : undefined,
                  }}
                  disableDragging={disableDrag}
                  enableResizing={!el.isStatic}
                  onResizeStart={elOnResizeStart}
                  onResize={elOnResize}
                  size={{ width: el.width, height: el.height }}
                  onResizeStop={elOnResizeStop}
                  onDragStart={elOnDragStart}
                  onDrag={elOnDrag}
                  onDragStop={elOnDragEnd}
                  onDrop={elOnDrop}
                  onDragOver={(e: any) => {
                    e.preventDefault();
                    return false;
                  }}
                  // title={el.tag}
                  default={{
                    x: el.x,
                    y: el.y,
                    width: el.width,
                    height: el.height,
                  }}
                  tabIndex={0}
                  onKeyDown={() => {}}
                  onClick={elOnClick}
                />
              );
            })
          : null}

        <ContextMenu
          x={coords.x}
          y={coords.y}
          isOpen={isContextMenuOpen && activeElements.length > 0}
          onClose={() => setContextMenuOpen(false)}
          items={
            activeElements.length === 1
              ? [
                  {
                    text: t("Duplicate"),
                    action: () => {
                      cloneActiveElement();
                    },
                  },
                  {
                    text: t("Edit"),
                    action: () => {
                      setEditedElement({ action: "edit" });
                    },
                  },
                  {
                    text: t("Bring to Top"),
                    action: () => {
                      moveZOrder("TOP");
                    },
                  },
                  {
                    text: t("Move Up"),
                    action: () => {
                      moveZ(1);
                    },
                  },
                  {
                    text: t("Move Down"),
                    action: () => {
                      moveZ(-1);
                    },
                  },
                  {
                    text: t("Delete"),
                    action: () => {
                      removeElement();
                    },
                  },
                ]
              : [
                  {
                    text: t("Align Top"),
                    action: () => {
                      alignElements("TOP");
                    },
                  },
                  {
                    text: t("Align Left"),
                    action: () => {
                      alignElements("LEFT");
                    },
                  },
                  {
                    text: t("Align Right"),
                    action: () => {
                      alignElements("RIGHT");
                    },
                  },
                  {
                    text: t("Align Bottom"),
                    action: () => {
                      alignElements("BOTTOM");
                    },
                  },
                  {
                    text: t("Distribute heights"),
                    action: () => {
                      alignElements("HEIGHT");
                    },
                  },
                ]
          }
        />
      </div>
    </>
  );
  /**
   * If you want to debug the editedElement action
   */
  //       <pre style={{ marginTop: '100px' }}>{JSON.stringify(editedElement, null, 2)}</pre>
};

export default RenderSelectedSlide;
